import { useMemo } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { EmissionTier, CanonicalPeriod, AdaptationView, MetricState } from '../../common'
import { usePropertyClimateData, isActiveMetricState } from '../../../../hooks/climateData'
import type { MetricDataFilters } from '../../../../hooks/analytics'
import { FloodScoreBadge } from '../../common/flood/utils'
import ClimateMetricCard from '../../common/ClimateMetricCard'
import ClimateComputationIndicator from '../../common/ClimateComputationIndicator'
import MetricStatePlaceholder from '../../common/MetricStatePlaceholder'
import { getMostRelevantMetricState, MetricValueFallback } from '../../common/MetricStateIndicator'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import { renderHelpLines } from '../../common/aggregateHelpText'
import {
  BI_DAYS_LABEL,
  DAMAGE_AAL_LABEL,
  FINANCIAL_BREAKDOWN_TITLE,
  formatCurrencyValue,
  formatDamageTargetLabel,
  formatFloodSourceLabel,
  formatPlainNumber,
  REVENUE_AAL_LABEL,
  sumMetricNode,
  TOTAL_AAL_LABEL,
} from '../../common/financialMetrics'
import { useCurrentUser } from '../../../../hooks/users'
import {
  getMetricKeys,
  getPeriodNode,
  getRecordKeys,
  getRecordValue,
  getSelectedPeriodPointY,
  getSucceededMetricData,
  getTotalAalValue,
  hasAnyCanonicalPeriodData,
  sumSelectedPeriodNode,
} from '../overviewMetrics'

const SCORE_METRIC_KEY = 'fld:score'
const DAMAGE_AAL_METRIC_KEY = 'fld:average_damage_loss'
const REVENUE_AAL_METRIC_KEY = 'fld:average_bi_loss'
const BI_DAYS_METRIC_KEY = 'fld:average_bi_days'

export default function FloodSummary({
  propertyId,
  emissionTier,
  selectedPeriod,
  view,
  filters,
}: {
  propertyId: number
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
  view?: AdaptationView
  filters?: MetricDataFilters | null
}) {
  const floodQueryParams = useMemo(() => ({ view, ...(filters ? { filters } : {}) }), [filters, view])
  const {
    data: scoreData,
    isLoading: scoreInitialLoading,
    isFetching: scoreFetching,
    error: scoreError,
  } = usePropertyClimateData(SCORE_METRIC_KEY, { propertyId }, floodQueryParams)
  const {
    data: damageAalData,
    isLoading: damageAalInitialLoading,
    isFetching: damageAalFetching,
    error: damageAalError,
  } = usePropertyClimateData(DAMAGE_AAL_METRIC_KEY, { propertyId }, floodQueryParams)
  const {
    data: revenueAalData,
    isLoading: revenueAalInitialLoading,
    isFetching: revenueAalFetching,
    error: revenueAalError,
  } = usePropertyClimateData(REVENUE_AAL_METRIC_KEY, { propertyId }, floodQueryParams)
  const {
    data: biDaysData,
    isLoading: biDaysInitialLoading,
    isFetching: biDaysFetching,
    error: biDaysError,
  } = usePropertyClimateData(BI_DAYS_METRIC_KEY, { propertyId }, floodQueryParams)
  const scoreLoading = scoreInitialLoading || scoreFetching
  const damageAalLoading = damageAalInitialLoading || damageAalFetching
  const revenueAalLoading = revenueAalInitialLoading || revenueAalFetching
  const biDaysLoading = biDaysInitialLoading || biDaysFetching

  const { currencySymbol } = useCurrentUser()

  const scorePayload = getSucceededMetricData(scoreData)
  const damageAalPayload = getSucceededMetricData(damageAalData)
  const revenueAalPayload = getSucceededMetricData(revenueAalData)
  const biDaysPayload = getSucceededMetricData(biDaysData)

  const scoreValue = getSelectedPeriodPointY(scorePayload, emissionTier, selectedPeriod)
  const adjustedScoreValue = scoreValue != null && !Number.isNaN(scoreValue) ? Math.round(scoreValue) : null

  const damageAalPeriodNode = getPeriodNode(damageAalPayload, emissionTier, selectedPeriod)
  const revenueAalPeriodNode = getPeriodNode(revenueAalPayload, emissionTier, selectedPeriod)
  const biDaysPeriodNode = getPeriodNode(biDaysPayload, emissionTier, selectedPeriod)

  const damageAalTotal = sumMetricNode(damageAalPeriodNode)
  const revenueAalTotal = sumMetricNode(revenueAalPeriodNode)
  const biDaysTotal = sumMetricNode(biDaysPeriodNode)
  const totalAalValue = getTotalAalValue(damageAalTotal, revenueAalTotal)
  const totalAalState = getMostRelevantMetricState([damageAalData?.state?.value, revenueAalData?.state?.value])

  const damageTargets = useMemo(() => {
    const explicit = getMetricKeys(damageAalPayload, 'target')
    if (explicit.length) return explicit
    return getRecordKeys(damageAalPeriodNode)
  }, [damageAalPayload, damageAalPeriodNode])

  const damageSources = useMemo(() => {
    const explicit = getMetricKeys(damageAalPayload, 'source')
    if (explicit.length) return explicit
    const firstTarget = damageTargets[0]
    if (firstTarget) return getRecordKeys(getRecordValue(damageAalPeriodNode, firstTarget))
    return []
  }, [damageAalPayload, damageAalPeriodNode, damageTargets])

  const revenueSources = useMemo(() => {
    const explicit = getMetricKeys(revenueAalPayload, 'source')
    if (explicit.length) return explicit
    return getRecordKeys(revenueAalPeriodNode)
  }, [revenueAalPayload, revenueAalPeriodNode])

  const damageBreakdownHasData = damageTargets.some(target =>
    damageSources.some(
      source => sumMetricNode(getRecordValue(getRecordValue(damageAalPeriodNode, target), source)) != null,
    ),
  )
  const revenueBreakdownHasData = revenueSources.some(
    source => sumMetricNode(getRecordValue(revenueAalPeriodNode, source)) != null,
  )

  const annualLoading = scoreLoading || damageAalLoading || revenueAalLoading || biDaysLoading
  const annualErrored = !!(scoreError && damageAalError && revenueAalError && biDaysError)
  const overviewStates = [scoreData, damageAalData, revenueAalData, biDaysData]
    .map(entry => entry?.state?.value)
    .filter((value): value is MetricState => Boolean(value))
  const overviewActive = overviewStates.some(isActiveMetricState)
  const overviewAllUnsucceeded =
    overviewStates.length > 0 && overviewStates.every(value => value !== MetricState.SUCCEEDED)
  const overviewTerminalState = overviewStates.includes(MetricState.FAILED) ? MetricState.FAILED : overviewStates[0]
  const hasAnyOverviewData = useMemo(
    () =>
      hasAnyCanonicalPeriodData([
        period => getSelectedPeriodPointY(scorePayload, emissionTier, period),
        period => sumSelectedPeriodNode(damageAalPayload, emissionTier, period),
        period => sumSelectedPeriodNode(revenueAalPayload, emissionTier, period),
        period => sumSelectedPeriodNode(biDaysPayload, emissionTier, period),
      ]),
    [biDaysPayload, damageAalPayload, emissionTier, revenueAalPayload, scorePayload],
  )
  const riskRatingHelpText = 'Flood risk rating for the selected horizon and emissions scenario.'
  const totalAalHelpText = renderHelpLines([
    `${TOTAL_AAL_LABEL} = Structure Damage AAL + Contents Damage AAL + BI AAL.`,
    'Shown for the selected horizon and emissions scenario.',
  ])
  const damageAalHelpText = 'Annualised structure and contents damage loss for the selected horizon.'
  const revenueAalHelpText = 'Annualised business interruption revenue loss for the selected horizon.'
  const biDaysHelpText = 'Expected business interruption days per year for the selected horizon.'

  if (annualLoading && !scoreData && !damageAalData && !revenueAalData && !biDaysData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (annualErrored) {
    return <Alert severity="error">Could not load flood summary data.</Alert>
  }

  return (
    <Box>
      <Box
        mb={2}
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}>
        <ClimateSectionTitle
          title="Overview"
          subtitle="Summary of flood risk for the selected period"
          loading={annualLoading}
          helpText="Overview metrics use the horizon selected in the page header."
        />
      </Box>

      {!hasAnyOverviewData ? (
        overviewActive ? (
          <ClimateComputationIndicator variant="block" minHeight={120} />
        ) : overviewAllUnsucceeded ? (
          <MetricStatePlaceholder state={overviewTerminalState} minHeight={120} />
        ) : (
          <Alert severity="info">No flood summary data is available for this property.</Alert>
        )
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <ClimateMetricCard title="Risk Rating" helpText={riskRatingHelpText} loading={scoreLoading}>
                {adjustedScoreValue != null ? (
                  <FloodScoreBadge score={adjustedScoreValue} size="compact" />
                ) : (
                  <MetricValueFallback
                    state={scoreData?.state?.value}
                    fallback="Flood risk rating is unavailable for this period."
                  />
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard
                title={TOTAL_AAL_LABEL}
                helpText={totalAalHelpText}
                loading={damageAalLoading || revenueAalLoading}>
                {totalAalValue != null ? (
                  <Typography variant="h4">{formatCurrencyValue(totalAalValue, currencySymbol, 0)}</Typography>
                ) : (
                  <MetricValueFallback
                    state={totalAalState}
                    fallback="Total AAL data is unavailable for this period."
                  />
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard title={BI_DAYS_LABEL} helpText={biDaysHelpText} loading={biDaysLoading}>
                {biDaysTotal != null ? (
                  <Typography variant="h4">{`${formatPlainNumber(biDaysTotal, 1)} days/year`}</Typography>
                ) : (
                  <MetricValueFallback
                    state={biDaysData?.state?.value}
                    fallback="Business interruption days are unavailable for this period."
                  />
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>

          <Box mt={4}>
            <ClimateSectionTitle
              title={FINANCIAL_BREAKDOWN_TITLE}
              subtitle="Damage and revenue components of annualised flood impacts"
              helpText="Financial metrics are annualised expected losses for the selected horizon and emissions scenario."
            />
          </Box>

          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <ClimateMetricCard title={DAMAGE_AAL_LABEL} helpText={damageAalHelpText} loading={damageAalLoading}>
                {damageAalTotal != null ? (
                  <Typography variant="h4">{formatCurrencyValue(damageAalTotal, currencySymbol, 0)}</Typography>
                ) : (
                  <MetricValueFallback
                    state={damageAalData?.state?.value}
                    fallback="Damage AAL is unavailable for this period."
                  />
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ClimateMetricCard title={REVENUE_AAL_LABEL} helpText={revenueAalHelpText} loading={revenueAalLoading}>
                {revenueAalTotal != null ? (
                  <Typography variant="h4">{formatCurrencyValue(revenueAalTotal, currencySymbol, 0)}</Typography>
                ) : (
                  <MetricValueFallback
                    state={revenueAalData?.state?.value}
                    fallback="BI AAL is unavailable for this period."
                  />
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={7}>
              <Card variant="outlined">
                <CardContent>
                  <ClimateSectionTitle
                    title="Damage AAL Breakdown"
                    subtitle="Structure and contents split across flood sources"
                    loading={damageAalLoading}
                    helpText="Breaks Damage AAL into structure and contents components by flood source."
                  />
                  <Box mt={2}>
                    {!damageBreakdownHasData ? (
                      <Typography variant="body2" color="text.secondary">
                        No damage breakdown is available for this period.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell />
                            {damageSources.map(source => (
                              <TableCell key={source} align="right">
                                {formatFloodSourceLabel(source)}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {damageTargets.map(target => (
                            <TableRow key={target}>
                              <TableCell component="th" scope="row">
                                {formatDamageTargetLabel(target)}
                              </TableCell>
                              {damageSources.map(source => {
                                const value = sumMetricNode(
                                  getRecordValue(getRecordValue(damageAalPeriodNode, target), source),
                                )
                                return (
                                  <TableCell key={`${target}-${source}`} align="right">
                                    {formatCurrencyValue(value, currencySymbol, 0)}
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card variant="outlined">
                <CardContent>
                  <ClimateSectionTitle
                    title="BI AAL Breakdown"
                    subtitle="Annual interruption-related revenue loss across flood sources"
                    loading={revenueAalLoading}
                    helpText="Breaks BI AAL into river, surface, and coastal flood source contributions where available."
                  />
                  <Box mt={2}>
                    {!revenueBreakdownHasData ? (
                      <Typography variant="h4">—</Typography>
                    ) : (
                      <Stack spacing={1}>
                        {revenueSources.map(source => {
                          const value = sumMetricNode(getRecordValue(revenueAalPeriodNode, source))
                          return (
                            <Stack key={source} direction="row" justifyContent="space-between" alignItems="baseline">
                              <Typography variant="body2" color="text.secondary">
                                {formatFloodSourceLabel(source)}
                              </Typography>
                              <Typography variant="body2">{formatCurrencyValue(value, currencySymbol, 0)}</Typography>
                            </Stack>
                          )
                        })}
                      </Stack>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}
